import type { DOMPurify as DOMPurifyType, RemovedAttribute, RemovedElement } from 'dompurify';
import { errorTracker } from '@/shared/lib/error-tracking';

type PurifyInstance = ReturnType<DOMPurifyType>;

const SANITIZER_CONFIG = {
  ALLOWED_TAGS: ['a', 'div', 'body', 'p', 'b', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'blockquote'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'title'],
};

let purifyInstance: PurifyInstance | null = null;
let purifyPromise: Promise<PurifyInstance> | null = null;

const createInstance = (DOMPurify: DOMPurifyType): PurifyInstance => {
  const p = DOMPurify(window as never);
  p.setConfig(SANITIZER_CONFIG);
  return p;
};

/**
 * Loads and configures the DOMPurify singleton. Call once during app boot to
 * prime it before any render-time `sanitize()`.
 */
export const initSanitizer = async (): Promise<void> => {
  if (purifyInstance) return;
  purifyPromise ??= import('dompurify').then((mod) => {
    purifyInstance = createInstance(mod.default);
    return purifyInstance;
  });
  await purifyPromise;
};

// Safe fallback when DOMPurify hasn't loaded yet: strip everything to text so we
// never emit unsanitized markup. In practice `initSanitizer()` runs at boot, so
// this path is not hit during normal operation.
const stripToText = (dirty: string): string => {
  const el = document.createElement('div');
  el.textContent = dirty;
  return el.innerHTML;
};

const runSanitize = (p: PurifyInstance, ...args: Parameters<PurifyInstance['sanitize']>): string => {
  let result: string;
  try {
    result = p.sanitize(...args) as string;
  } catch (sanitizeError) {
    errorTracker.captureException(sanitizeError, {
      extra: { type: 'sanitizer_failure' },
    });
    return '';
  }

  const removedElements = p.removed.filter((x) => Boolean(Object.keys(x).length));
  if (removedElements.length) {
    errorTracker.captureMessage('Possible XSS attempt detected', 'warning', {
      extra: {
        type: 'xss_detected',
        removed: removedElements.map((entry: RemovedElement | RemovedAttribute) => {
          if ('element' in entry && entry.element instanceof Element) {
            return { element: entry.element.tagName };
          }
          if ('attribute' in entry && entry.attribute instanceof Attr) {
            return {
              attribute: entry.attribute.name,
              from: entry.from instanceof Element ? entry.from.tagName : undefined,
            };
          }
          return 'unknown';
        }),
      },
    });
  }

  return result;
};

const sanitizer = () => {
  // Kick off the load so a first-call-before-prime path still resolves ASAP.
  if (!purifyInstance) void initSanitizer();

  return {
    sanitize: (...args: Parameters<PurifyInstance['sanitize']>): string => {
      if (!purifyInstance) return stripToText(String(args[0] ?? ''));
      return runSanitize(purifyInstance, ...args);
    },
  };
};

export default sanitizer;
